import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Skeleton,
    Box,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import {
    Search as SearchIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import DottedArrowIcon from '../../components/DottedArrowIcon';
import api from '../../utils/api';
import Footer from '../../components/Footer/Footer';
import DataTablePagination from '../../components/Table/DataTablePagination';
import ListFilters from '../../components/Tasks/ListFilters';
import { filtersFromSearchParams, filtersToSearchParams, filtersToApiParams } from '../../utils/listFilterParams';
import { useAuth } from '../../context/AuthContext';
import { formatWaiting, isOverdue } from '../../components/Tasks/taskColumns';
import StatusChip from '../../components/Tasks/StatusChip';

const bounceRight = keyframes`
  0%, 100% { transform: translateX(0px); }
  50% { transform: translateX(5px); }
`;

const AllTasks = () => {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({ total: 0, pages: 1, limit: 10 });
    // The old `selectedRows` state had no setter and no checkbox column, so the
    // footer it fed could only ever read "0 of N selected". Removed rather than
    // wired up — acting on many applications at once is a policy decision, not
    // a UI affordance, and belongs in its own change.
    const { user } = useAuth();
    // See Reports.js: the server scopes management to its own department; this
    // only stops the page offering a filter the server will override.
    const pinnedDepartment = user?.role === 'management' ? (user.department || '') : null;
    // Status, stage, department and dates live in the URL, shared with the
    // Suppliers page, so a filtered queue can be bookmarked or sent on.
    const [searchParams, setSearchParams] = useSearchParams();
    const filterKey = searchParams.toString();
    const filters = useMemo(() => filtersFromSearchParams(searchParams), [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const handleFiltersChange = (next) => {
        setSearchParams(filtersToSearchParams(next, searchParams), { replace: true });
        setPage(1);
    };
    const [sortOrder, setSortOrder] = useState('desc');

    useEffect(() => {
        fetchTasks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, search, filterKey, sortOrder]);

    const fetchTasks = async () => {
        try {
            setLoading(true);
            const response = await api.get('/dashboard/all-tasks', {
                params: {
                    page, limit: rowsPerPage, search, sortOrder,
                    ...filtersToApiParams(filters),
                    ...(pinnedDepartment ? { department: pinnedDepartment } : {}),
                }
            });

            if (response.data.success) {
                setTasks(response.data.data || []);
                setPagination(response.data.pagination || { total: 0, pages: 1, limit: 10 });
            }
        } catch (err) {
            console.error('Error fetching all tasks:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleRowClick = (task) => {
        if (task._id) {
            navigate(`/suppliers/${task._id}`);
        }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', pb: 0 }}>
            <Container
                maxWidth="lg"
                sx={{
                    pt: { xs: 3, sm: 5 },
                    pb: { xs: 3, sm: 4 },
                    px: { xs: 2, sm: 3 },
                    flex: 1,
                }}
            >
                {/* Page Header */}
                <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 'bold',
                                mb: 1,
                                color: '#111827',
                                fontSize: { xs: '22px', sm: '28px' },
                                letterSpacing: '-0.01em',
                            }}
                        >
                            All tasks
                        </Typography>
                        <Typography
                            sx={{
                                color: '#6b7280',
                                fontSize: { xs: '13px', sm: '14px' },
                                lineHeight: 1.6,
                            }}
                        >
                            View and management all supplier onboarding tasks here
                        </Typography>
                    </Box>

                    {/* Search, Filter and Download */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        mb: 3,
                        gap: 2,
                        flexWrap: 'wrap',
                    }}>
                        <TextField
                            placeholder="Search"
                            value={search}
                            onChange={handleSearchChange}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                flex: 1,
                                minWidth: { xs: '100%', sm: '180px' },
                                maxWidth: { xs: '100%', sm: '280px' },
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#fff',
                                    borderRadius: '8px',
                                    '& fieldset': { borderColor: '#e5e7eb' },
                                    '&:hover fieldset': { borderColor: '#9ca3af' },
                                    '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: '1px' }
                                }
                            }}
                        />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <ListFilters filters={filters} onChange={handleFiltersChange} pinnedDepartment={pinnedDepartment} />
                    </Box>
                </Box>

                {/* Tasks Table */}
                {loading ? (
                    <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', p: 2 }}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton key={i} height={44} sx={{ mb: 0.5 }} />
                        ))}
                    </Box>
                ) : (
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            backgroundColor: '#fff'
                        }}
                    >
                        <Table sx={{ minWidth: { xs: 620, md: 760 } }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                    <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', py: 1.5 }}>ID</TableCell>
                                    <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', py: 1.5 }}>Company Name</TableCell>
                                    <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Entity Type</TableCell>
                                    <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Department</TableCell>
                                    <TableCell sortDirection={sortOrder} sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', py: 1.5 }}>
                                        <TableSortLabel
                                            active
                                            direction={sortOrder}
                                            onClick={() => { setSortOrder(o => (o === 'asc' ? 'desc' : 'asc')); setPage(1); }}
                                            sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', whiteSpace: 'nowrap' }}
                                        >
                                            Submission Date
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', py: 1.5 }}>Waiting</TableCell>
                                    <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', py: 1.5 }}>Status</TableCell>
                                    <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', py: 1.5, display: { xs: 'none', sm: 'table-cell' } }}>Stage</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', py: 1.5 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tasks.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '13px' }}>
                                                No tasks found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    tasks.map((task) => (
                                        <TableRow
                                            key={task._id}
                                            hover
                                            onClick={() => handleRowClick(task)}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '&:hover': {
                                                    backgroundColor: '#fcfcfc'
                                                }
                                            }}
                                        >
                                            <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5 }}>
                                                {task.taskId}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5 }}>
                                                {task.supplierName}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>
                                                {task.entityType}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '14px', py: 1.5, display: { xs: 'none', md: 'table-cell' }, color: task.department ? '#111827' : '#898781', fontStyle: task.department ? 'normal' : 'italic' }}>
                                                {task.department || 'Unassigned'}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5 }}>
                                                {formatDate(task.submissionDate)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '14px', py: 1.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: isOverdue(task.waitingDays) ? '#d03b3b' : '#111827', fontWeight: isOverdue(task.waitingDays) ? 600 : 400 }}>
                                                {formatWaiting(task.waitingDays)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5 }}>
                                                <StatusChip status={task.rawStatus} label={task.status} />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5, display: { xs: 'none', sm: 'table-cell' } }}>
                                                {task.stageLabel || '-'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.5 }}>
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        color: '#578A18',
                                                        animation: `${bounceRight} 1.2s ease-in-out infinite`,
                                                        '&:hover': {
                                                            color: '#467014',
                                                            backgroundColor: 'transparent',
                                                            animationDuration: '0.5s',
                                                        },
                                                    }}
                                                >
                                                    <DottedArrowIcon size={18} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>

                        <DataTablePagination
                            page={page}
                            total={pagination.total}
                            rowsPerPage={rowsPerPage}
                            onPageChange={setPage}
                            onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(1); }}
                            rowsPerPageOptions={[10, 25, 50]}
                            loading={loading}
                        />
                    </TableContainer>
                )}
            </Container>
            <Footer />
        </Box>
    );
};

export default AllTasks;
